import api from './axios';

// All requests here automatically get the admin token
// because axios.js checks if the URL starts with /admin

/* ══════════════════════════════════════════════════════════════
   DASHBOARD
══════════════════════════════════════════════════════════════ */
export const getAdminStats = async () => {
  const { data } = await api.get('/admin/stats');
  return data;
};

/* ══════════════════════════════════════════════════════════════
   VOCABULARY
══════════════════════════════════════════════════════════════ */
/* ── Full analytics: real counts, cohorts and activity ──────────
   GET /api/admin/analytics
   🔒 Requires admin token
─────────────────────────────────────────────────────────────── */
export const getAdminAnalytics = async () => {
  const { data } = await api.get('/admin/analytics');
  return data;
};

/* ── Site configuration: live levels + page availability ────────
   GET / PUT /api/admin/config
   🔒 Requires admin token
─────────────────────────────────────────────────────────────── */
export const adminGetConfig = async () => {
  const { data } = await api.get('/admin/config');
  return data;
};

export const adminUpdateConfig = async (body) => {
  const { data } = await api.put('/admin/config', body);
  return data;
};

export const adminGetVocab = async (params = {}) => {
  const { data } = await api.get('/admin/vocabulary', { params });
  return data;
};
export const adminCreateVocab = async (body) => {
  const { data } = await api.post('/admin/vocabulary', body);
  return data;
};
export const adminUpdateVocab = async (id, body) => {
  const { data } = await api.put(`/admin/vocabulary/${id}`, body);
  return data;
};
export const adminDeleteVocab = async (id) => {
  const { data } = await api.delete(`/admin/vocabulary/${id}`);
  return data;
};

/* ══════════════════════════════════════════════════════════════
   KANA  (Hiragana + Katakana)
══════════════════════════════════════════════════════════════ */
export const adminGetKanaList = async (params = {}) => {
  const { data } = await api.get('/admin/kana', { params });
  return data;
};
export const adminCreateKana = async (body) => {
  const { data } = await api.post('/admin/kana', body);
  return data;
};
export const adminUpdateKana = async (id, body) => {
  const { data } = await api.put(`/admin/kana/${id}`, body);
  return data;
};
export const adminDeleteKana = async (id) => {
  const { data } = await api.delete(`/admin/kana/${id}`);
  return data;
};

/* ══════════════════════════════════════════════════════════════
   KANJI
══════════════════════════════════════════════════════════════ */
export const adminGetKanji = async (params = {}) => {
  const { data } = await api.get('/admin/kanji', { params });
  return data;
};
export const adminCreateKanji = async (body) => {
  const { data } = await api.post('/admin/kanji', body);
  return data;
};
export const adminUpdateKanji = async (id, body) => {
  const { data } = await api.put(`/admin/kanji/${id}`, body);
  return data;
};
export const adminDeleteKanji = async (id) => {
  const { data } = await api.delete(`/admin/kanji/${id}`);
  return data;
};

/* ══════════════════════════════════════════════════════════════
   QUIZ QUESTIONS
══════════════════════════════════════════════════════════════ */
export const adminGetQuiz = async (params = {}) => {
  const { data } = await api.get('/admin/quiz', { params });
  return data;
};
export const adminCreateQuiz = async (body) => {
  const { data } = await api.post('/admin/quiz', body);
  return data;
};
export const adminUpdateQuiz = async (id, body) => {
  const { data } = await api.put(`/admin/quiz/${id}`, body);
  return data;
};
export const adminDeleteQuiz = async (id) => {
  const { data } = await api.delete(`/admin/quiz/${id}`);
  return data;
};

/* ══════════════════════════════════════════════════════════════
   USERS
══════════════════════════════════════════════════════════════ */
export const adminGetUsers = async (params = {}) => {
  const { data } = await api.get('/admin/users', { params });
  return data;
};
export const adminGetUser = async (id) => {
  const { data } = await api.get(`/admin/users/${id}`);
  return data;
};
export const adminUpdateUserStatus = async (id, status) => {
  const { data } = await api.patch(`/admin/users/${id}/status`, { status });
  return data;
};
export const adminDeleteUser = async (id) => {
  const { data } = await api.delete(`/admin/users/${id}`);
  return data;
};

/* ══════════════════════════════════════════════════════════════
   ANNOUNCEMENTS
══════════════════════════════════════════════════════════════ */
export const adminGetAnnouncements = async () => {
  const { data } = await api.get('/admin/announcements');
  return data;
};
export const adminCreateAnnouncement = async (body) => {
  const { data } = await api.post('/admin/announcements', body);
  return data;
};
export const adminUpdateAnnouncement = async (id, body) => {
  const { data } = await api.put(`/admin/announcements/${id}`, body);
  return data;
};
export const adminDeleteAnnouncement = async (id) => {
  const { data } = await api.delete(`/admin/announcements/${id}`);
  return data;
};
/* ══════════════════════════════════════════════════════════════
   CAMPAIGNS — bulk mail to the pre-registration waitlist
══════════════════════════════════════════════════════════════ */

/* GET /api/admin/campaign/recipients   🔒 admin */
export const adminGetCampaignRecipients = async () => {
  const { data } = await api.get('/admin/campaign/recipients');
  return data;
};

/* POST /api/admin/campaign/send   🔒 admin
   Returns a job id straight away — the run happens in the background.
   No long-poll, so no timeout to tune. */
export const adminSendCampaign = async (body) => {
  const { data } = await api.post('/admin/campaign/send', body);
  return data;
};

/* GET /api/admin/campaign/jobs/:id   🔒 admin */
export const adminGetCampaignJob = async (id) => {
  const { data } = await api.get(`/admin/campaign/jobs/${id}`);
  return data;
};

/* POST /api/admin/campaign/jobs/:id/cancel   🔒 admin */
export const adminCancelCampaignJob = async (id) => {
  const { data } = await api.post(`/admin/campaign/jobs/${id}/cancel`);
  return data;
};

/* POST /api/admin/campaign/recipients   🔒 admin
   Adds a typed address to the waitlist so it becomes selectable. It is
   stored rather than passed through, which is what lets the send
   endpoint keep refusing anyone who is not on the list. */
export const adminAddCampaignRecipient = async (email) => {
  const { data } = await api.post('/admin/campaign/recipients', { email });
  return data;
};

/* DELETE /api/admin/campaign/recipients/:id   🔒 admin */
export const adminRemoveCampaignRecipient = async (id) => {
  const { data } = await api.delete(`/admin/campaign/recipients/${id}`);
  return data;
};
